import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, Save } from 'lucide-react';
import { COMMON_TIMEZONES, allTimeZones, previewBookingWindow, formatSwitchTime } from '@/lib/propertyTime';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const TimeSettingsDialog = ({ property, open, onOpenChange, onSaved }) => {
  const [tz, setTz] = useState('America/New_York');
  const [switchTime, setSwitchTime] = useState('14:00');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (property) {
      setTz(property.time_zone || 'America/New_York');
      setSwitchTime(property.next_day_switch_time || '14:00');
      setSearch('');
    }
  }, [property]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const otherZones = useMemo(() => {
    const common = new Set(COMMON_TIMEZONES.map((z) => z.value));
    const q = search.trim().toLowerCase();
    return allTimeZones().filter((z) => !common.has(z) && (!q || z === tz || z.toLowerCase().includes(q)));
  }, [search, tz]);

  const preview = previewBookingWindow(tz, switchTime);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/properties/${property.id}/time-settings`,
        { time_zone: tz, next_day_switch_time: switchTime },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Time settings saved');
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error('Failed to save', { description: error.response?.data?.detail || 'Please try again' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="time-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#9F1239]" /> Time & Booking Window — {property?.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div>
            <Label htmlFor="tz-search">Time zone</Label>
            <Input id="tz-search" placeholder="Search all time zones..." value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2" data-testid="input-tz-search" />
            <select value={tz} onChange={(e) => setTz(e.target.value)} className="w-full px-3 py-2 border border-[#E7E5E4] rounded-sm bg-white" data-testid="select-time-zone">
              <optgroup label="Common US zones">
                {COMMON_TIMEZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
              </optgroup>
              <optgroup label="All time zones">
                {otherZones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <Label htmlFor="switch-time">Next-day switch time</Label>
            <Input id="switch-time" type="time" value={switchTime} onChange={(e) => setSwitchTime(e.target.value)} required data-testid="input-switch-time" />
            <p className="text-xs text-[#78716C] mt-1">From {formatSwitchTime(switchTime)} onward, the booking page opens on tomorrow. Guests can still pick today.</p>
          </div>
          <div className="bg-[#F5F5F4] p-3 rounded-sm text-sm text-[#1C1917]" data-testid="time-settings-preview">
            Now at the property: <span className="font-semibold">{preview.nowLabel}</span> — booking page opens on <span className="font-semibold">{preview.opensOn}</span>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#9F1239] hover:bg-[#881337]" data-testid="btn-save-time-settings">
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
